/**
 * @fileoverview Main React Application Component
 * @module App
 * 
 * Business Context:
 * - Main application router and authentication provider
 * - Handles routing between authentication and dashboard states
 * - Provides Florida-focused branding and user experience
 * - 4-Step course journey: Document Your Home -> File Appeal -> Find Flaws -> Talk to Assessor
 * 
 * Design Context:
 * - MasterClass-inspired design with ocean color palette
 * - Responsive design optimized for homeowner demographic
 * - Smooth transitions between authentication and dashboard states
 * 
 * NOTE: Public pages (landing, privacy, terms) are now served by Next.js at appealyourhome.com
 * This SPA handles authenticated routes at app.appealyourhome.com
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthPage } from './pages/AuthPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import PreCheckoutSignupPage from './pages/PreCheckoutSignupPage';
import StepsDashboardPage from './pages/StepsDashboardPage';
import Step1FilePage from './pages/Step1FilePage';
import Step2DocumentPage from './pages/Step2DocumentPage';
import Step3FlawsPage from './pages/Step3FlawsPage';
import MaintenanceActivityPage from './pages/MaintenanceActivityPage';
import ArgumentsPage from './pages/ArgumentsPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import AccountSetupPage from './pages/AccountSetupPage';
import CheckEmailPage from './pages/CheckEmailPage';
import ProfilePage from './pages/ProfilePage';
import GettingStartedPage from './pages/GettingStartedPage';
import { ProtectedRoute } from './components/common/ProtectedRoute';

// Landing page URL (Next.js at appealyourhome.com)
const LANDING_URL = import.meta.env.VITE_LANDING_URL || 'https://appealyourhome.com';

/**
 * Component to redirect to the public Next.js site
 * Used for pages that have been moved to the SEO-optimized public site
 */
const RedirectToPublicSite: React.FC<{ path: string }> = ({ path }) => {
  React.useEffect(() => {
    window.location.href = `${LANDING_URL}${path}`;
  }, [path]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Redirecting...</p>
      </div>
    </div>
  );
};

/**
 * App Router Component - Handles authentication-based routing
 */
const AppRouter: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Appeal Your Home...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing Page - Redirect to Next.js public site */}
      <Route 
        path="/" 
        element={
          <RedirectToPublicSite path="/" />
        } 
      />
      
      {/* Pre-Checkout Signup Route - Account creation before payment */}
      {/* Note: Don't redirect authenticated users - let PreCheckoutSignupPage handle checkout flow */}
      <Route 
        path="/signup" 
        element={<PreCheckoutSignupPage />} 
      />
      
      {/* Authentication Route */}
      <Route 
        path="/auth" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <AuthPage />
        } 
      />
      
      {/* OAuth Callback Route - Handles Google OAuth redirects */}
      <Route 
        path="/auth/oauth-callback" 
        element={<OAuthCallbackPage />} 
      />
      
      {/* Main Dashboard - 4-Step Journey */}
      <Route 
        path="/dashboard" 
        element={
          isAuthenticated ? <StepsDashboardPage /> : <Navigate to="/auth" replace />
        } 
      />
      
      {/* Redirect /steps to /dashboard for consistency */}
      <Route 
        path="/steps" 
        element={<Navigate to="/dashboard" replace />}
      />
      
      {/* Getting Started - Course Introduction Module */}
      <Route 
        path="/intro/getting-started" 
        element={
          <ProtectedRoute>
            <GettingStartedPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Step 1: Document Your Home's Issues - Video + Activity Hub */}
      <Route 
        path="/step/1/document" 
        element={
          <ProtectedRoute>
            <Step2DocumentPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Step 2: File Your Appeal */}
      <Route 
        path="/step/2/file-appeal" 
        element={
          <ProtectedRoute>
            <Step1FilePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Step 1: Maintenance Activity (Checklists) */}
      <Route 
        path="/maintenance" 
        element={
          <ProtectedRoute>
            <MaintenanceActivityPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Step 3: Find Flaws in County's Evidence */}
      <Route 
        path="/step/3/find-flaws" 
        element={
          <ProtectedRoute>
            <Step3FlawsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Step 4: Talk with the Assessor - Arguments Page */}
      <Route 
        path="/step/4/arguments" 
        element={
          <ProtectedRoute>
            <ArgumentsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Legacy Arguments Route (redirect to step route) */}
      <Route 
        path="/arguments" 
        element={<Navigate to="/step/4/arguments" replace />}
      />
      
      {/* Legacy course routes - redirect to dashboard */}
      <Route 
        path="/course/:moduleId" 
        element={<Navigate to="/dashboard" replace />}
      />
      <Route 
        path="/course/step/:stepNumber" 
        element={<Navigate to="/dashboard" replace />}
      />
      
      {/* Pricing & Payment Routes */}
      <Route 
        path="/pricing" 
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/payment/failure" 
        element={
          <ProtectedRoute>
            <PaymentFailurePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Check Email Route (Guest Purchase Completion - Step 1) */}
      {/* Keep in SPA - uses URL params from Stripe checkout */}
      <Route 
        path="/check-email" 
        element={<CheckEmailPage />}
      />
      
      {/* Account Setup Route (Guest Purchase Completion - Step 2) */}
      <Route 
        path="/account-setup" 
        element={<AccountSetupPage />}
      />
      
      {/* Legal Pages - Redirect to Next.js public site */}
      <Route 
        path="/privacy" 
        element={<RedirectToPublicSite path="/privacy" />}
      />
      
      <Route 
        path="/terms" 
        element={<RedirectToPublicSite path="/terms" />}
      />
      
      {/* User Profile Route */}
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Property Setup Route (Future) */}
      <Route 
        path="/property-setup" 
        element={
          <ProtectedRoute>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Property Setup</h1>
                <p className="text-gray-600">Property wizard coming soon...</p>
              </div>
            </div>
          </ProtectedRoute>
        } 
      />
      
      {/* 404 Route */}
      <Route 
        path="*" 
        element={
          <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-gray-600 mb-8">Page not found</p>
              <a 
                href={isAuthenticated ? "/dashboard" : "/"}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go {isAuthenticated ? "Home" : "to Homepage"}
              </a>
            </div>
          </div>
        } 
      />
    </Routes>
  );
};

/**
 * Main App Component with Authentication Provider
 */
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#ffffff',
              color: '#1e293b', // slate-800
              fontFamily: 'Montserrat, sans-serif',
              borderRadius: '12px',
              padding: '14px 20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e2e8f0', // slate-200
              fontSize: '14px',
              maxWidth: '420px',
            },
            success: {
              style: {
                borderLeft: '4px solid #14b8a6', // teal-500 - matches UI accent
              },
              iconTheme: { primary: '#14b8a6', secondary: '#fff' },
            },
            error: {
              duration: 5000,
              style: {
                borderLeft: '4px solid #ef4444', // red-500
              },
              iconTheme: { primary: '#ef4444', secondary: '#fff' },
            },
          }}
        />
        <AppRouter />
      </AuthProvider>
    </Router>
  );
};

export default App; 